import React, { useCallback } from 'react';
import _ from 'lodash';
import { useAccount, useBalance } from 'wagmi';
import { Button } from '../../shared/ui/Button/Button';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import {
  getTokenHumanReadableNameByChain,
  NETWORK_TO_CHAIN_ID_MAP,
} from '../bill/lib';
import PayERC20Button from '../../widgets/Payments/PayERC20Button';
import erc20TokensData from '../../shared/constants/erc20TokensData';
import { CryptoPayoutDetails } from './PayoutDetails';
import { useSafeWallet } from '../../shared/hooks/safeWallet';
import { AragonAppWithdrawProposalButton } from '../../widgets/Payments/CryptoToFiatPaymentModal';
import { useTokenBalance as useSolanaTokenBalance } from '../solana/useTokenBalance';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useTransferPayout } from '../solana/useTransferPayout';

/** This button can make following transfers:
 *  - aragon app withdrawal
 *  - ERC20 Token transfer
 *  - Solana Token transfer
 *  We think about this button as "Pay From Any Type of Wallet (source)"
 */

interface Props {
  disabled?: boolean;
  cryptoPaymentProviderId:
    | 'aragon-app'
    | 'wallet-connect'
    | 'solana-wallet-connect';

  cryptoPaymentProviderOptions?: {
    aragonAppAddress?: string;
  };

  payouts: CryptoPayoutDetails[];

  onTransferStart?: () => void;
  onTransferSuccess: (successData: {
    transactionHash?: string;
    safeTransactionHash?: string;
    aragonProposalId?: string;
  }) => void;

  onTransferFail?: (errorData: { error: any }) => void;
}

const MakeERC20CryptoTransferButton: React.FC<Props> = ({
  disabled,
  payouts,
  onTransferSuccess,
  onTransferStart,
}) => {
  const { isConnectedWalletSafe } = useSafeWallet();
  const { isConnected } = useAccount();
  const [payout] = payouts;

  const tokenAddress =
    // @ts-ignore
    erc20TokensData[payout.currency]?.erc20Address[payout.network];

  const _onWaitingForUserWalletConfirmation = (flag: boolean) => {
    if (flag) {
      onTransferStart();
    }
  };

  const _onReceiveTransactionHash = (transactionHash: string) => {
    if (isConnectedWalletSafe()) {
      onTransferSuccess({
        safeTransactionHash: transactionHash,
      });
    } else {
      onTransferSuccess({
        transactionHash: transactionHash,
      });
    }
  };

  if (!isConnected) {
    return (
      <ConnectButton.Custom>
        {({ openConnectModal }) => (
          <Button onClick={openConnectModal} fullSized color="indigo">
            Connect Your Wallet
          </Button>
        )}
      </ConnectButton.Custom>
    );
  }

  return (
    <PayERC20Button
      addressTo={payout.walletAddress}
      amountERC20={payout.amountNumber}
      tokenName={getTokenHumanReadableNameByChain(
        payout.currency,
        payout.network
      )}
      tokenAddress={tokenAddress}
      network={payout.network}
      disabled={disabled}
      payButtonStyles="flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      onUserApproval={_onWaitingForUserWalletConfirmation}
      setTransactionHash={_onReceiveTransactionHash}
      handleSuccess={_.noop}
      setTransactionInitiated={_.noop}
      setTransactionSuccessful={_.noop}
    />
  );
};

const MakeAragonAppCryptoTransferButton: React.FC<Props> = ({
  disabled,
  payouts,
  onTransferStart,
  onTransferSuccess,
  cryptoPaymentProviderOptions,
}) => {
  const { isConnected } = useAccount();

  const _handleCreateProposal = ({ proposalId }: { proposalId: string }) => {
    onTransferSuccess({
      aragonProposalId: proposalId,
    });
  };

  if (!isConnected) {
    return (
      <ConnectButton.Custom>
        {({ openConnectModal }) => (
          <Button onClick={openConnectModal} fullSized color="indigo">
            Connect Your Wallet
          </Button>
        )}
      </ConnectButton.Custom>
    );
  }

  return (
    <AragonAppWithdrawProposalButton
      disabled={disabled}
      onStart={onTransferStart}
      aragonAppAddress={cryptoPaymentProviderOptions?.aragonAppAddress}
      payouts={payouts}
      onSuccess={_handleCreateProposal}
      onFail={_.noop}
    />
  );
};

const MakeSolanaCryptoTransferButton: React.FC<Props> = ({
  disabled,
  onTransferStart,
  onTransferSuccess,
  onTransferFail,
  payouts,
}) => {
  const { connected, wallet } = useWallet();
  const [payout] = payouts;
  const { transferPayout } = useTransferPayout(payout);

  const _handlePayment = async () => {
    onTransferStart();
    transferPayout(null)
      .then((res) => {
        onTransferSuccess({
          transactionHash: res?.signature,
        });
      })
      .catch((error) => {
        onTransferFail &&
          onTransferFail({
            error,
          });
      });
  };

  if (!connected) {
    return (
      <WalletMultiButton
        style={{ background: 'none', padding: '0', width: '100%' }}>
        <Button className="w-[100%]" color="indigo">
          {wallet ? 'Connect Wallet' : 'Select Wallet'}
        </Button>
      </WalletMultiButton>
    );
  }

  return (
    <Button
      color="indigo"
      className="flex-1"
      disabled={disabled}
      onClick={_handlePayment}>
      Pay {payout.amountStr} {payout.currency}
    </Button>
  );
};

export const MakeCryptoTransferButton: React.FC<Props> = (props) => {
  if (props.cryptoPaymentProviderId === 'wallet-connect') {
    return <MakeERC20CryptoTransferButton {...props} />;
  }

  if (props.cryptoPaymentProviderId === 'aragon-app') {
    return <MakeAragonAppCryptoTransferButton {...props} />;
  }

  if (props.cryptoPaymentProviderId === 'solana-wallet-connect') {
    return <MakeSolanaCryptoTransferButton {...props} />;
  }

  return null;
};

export const useTokenBalanceByWalletProvider = (
  walletProvider: 'wallet-connect' | 'solana-wallet-connect',
  token: string,
  network: string
) => {
  const { address, chain } = useAccount();
  const { balanceData } = useSolanaTokenBalance(token);
  const chainId = NETWORK_TO_CHAIN_ID_MAP[network];

  const tokenAddress =
    // @ts-ignore
    erc20TokensData[token]?.erc20Address[network];

  const { data: tokenBalance } = useBalance({
    address,
    chainId,
    token: tokenAddress,
  });

  const getReadableBalanceString = useCallback(() => {
    if (walletProvider === 'wallet-connect') {
      return tokenBalance?.formatted;
    }

    if (walletProvider === 'solana-wallet-connect') {
      return balanceData?.uiAmountString;
    }

    return '';
  }, [walletProvider, tokenBalance, balanceData]);

  return {
    getReadableBalanceString,
  };
};
